import { useSuspenseQuery } from "@apollo/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PrimaryFullScreenSpinner,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { CustodyAccountCard } from "~/components/Custody/CustodyAccountCard";
import { EntityLink } from "~/components/link/EntityLink/EntityLink";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/ledger/accounts_/$accountId/")({
  head: () => opsHead("Ledger Account"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const ledgerAccountDetailQuery = graphql(`
  query AdminLedgerAccountDetail($accountId: ID!) {
    node(id: $accountId) {
      id
      ... on AssetLedgerAccount {
        name
        assetLedgerAccountKind
        asset {
          id
        }
      }
      ... on BeneficiaryLedgerAccount {
        name
        beneficiaryAccount {
          id
          accountName
        }
      }
      ... on OrganisationLedgerAccount {
        name
        organisation {
          id
          name
        }
      }
      ... on FXMarginProviderLedgerAccount {
        name
      }
      ...on CustodyLedgerAccount {
          name
          custodyAccount {
              ...CustodyAccountCard
          }
      }
    }
  }
`);

function View() {
  const { accountId } = Route.useParams();
  if (!accountId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(ledgerAccountDetailQuery, {
    variables: { accountId },
  });

  const ledgerAccount = data?.node;
  if (!ledgerAccount) {
    throw new NotFoundError();
  }

  return (
    <div className="flex flex-row gap-4">
      <div className="flex basis-2/3 flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-[160px_1fr] gap-y-4 py-2">
              <div className="text-sm text-tertiary">Account ID</div>
              <div className="text-sm">
                <pre className="bg-neutral-muted px-1">{ledgerAccount.id}</pre>
              </div>

              <div className="text-sm text-tertiary">Name</div>
              <div className="text-sm">
                {"name" in ledgerAccount ? ledgerAccount.name : "Unknown"}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex basis-1/3 flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Linked To</CardTitle>
            <CardDescription>
              Ledger accounts can be linked to business entities such as assets.
              Each ledger account can be linked to at most one business entity.
              One business entity can be linked to multiple ledger accounts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              if (ledgerAccount.__typename === "AssetLedgerAccount") {
                if (!ledgerAccount.asset) {
                  return null;
                }
                return (
                  <EntityLink
                    to={`/assets/${ledgerAccount.asset.id}`}
                    className="flex flex-col rounded-md border border-dotted p-2"
                  >
                    <div>Asset</div>
                    <div className="text-sm text-tertiary">
                      {`Tracking: ${ledgerAccount.assetLedgerAccountKind}`}
                    </div>
                    <div className="text-sm text-tertiary">
                      {ledgerAccount.asset.id}
                    </div>
                  </EntityLink>
                );
              }
              if (ledgerAccount.__typename === "BeneficiaryLedgerAccount") {
                if (!ledgerAccount.beneficiaryAccount) {
                  return null;
                }
                return (
                  <EntityLink
                    to={`/beneficiary-accounts/${ledgerAccount.beneficiaryAccount.id}`}
                    className="flex flex-col rounded-md border border-dotted p-2"
                  >
                    <div>Beneficiary Account</div>
                    <div className="text-sm text-tertiary">
                      {ledgerAccount.beneficiaryAccount.accountName}
                    </div>
                    <div className="text-sm text-tertiary">
                      {ledgerAccount.beneficiaryAccount.id}
                    </div>
                  </EntityLink>
                );
              }
              if (ledgerAccount.__typename === "OrganisationLedgerAccount") {
                if (!ledgerAccount.organisation) {
                  return null;
                }
                return (
                  <EntityLink
                    to={`/organisations/${ledgerAccount.organisation.id}`}
                    className="flex flex-col rounded-md border border-dotted p-2"
                  >
                    <div>Organisation</div>
                    <div className="text-sm text-tertiary">
                      {ledgerAccount.organisation.name}
                    </div>
                    <div className="text-sm text-tertiary">
                      {ledgerAccount.organisation.id}
                    </div>
                  </EntityLink>
                );
              }
              if (
                ledgerAccount.__typename === "FXMarginProviderLedgerAccount"
              ) {
                return <div className="text-sm">FX margin provider</div>;
              }
              if (ledgerAccount.__typename === "CustodyLedgerAccount") {
                if (ledgerAccount.custodyAccount) {
                  return (
                    <CustodyAccountCard
                      link
                      custodyAccount={ledgerAccount.custodyAccount}
                    />
                  );
                }
                return "Error loading custody account";
              }
              return (
                <div className="text-sm">
                  This ledger account is not linked to an entity.
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
